import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Filter } from 'src/app/core/interfaces/filter.interface';
import {
  PaginatedLookupRequest,
  PaginationResponse,
  SimpleLookupDto,
  LookupType
} from 'src/app/core/interfaces/lookup.interface';

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  private baseApiUrl = `${environment.apiBaseUrl}`;

  constructor(private http: HttpClient) { }

  // Generic method for all lookup types
  getList(lookupType: LookupType, request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.http.post<PaginationResponse<SimpleLookupDto>>(
      `${this.baseApiUrl}/Lookup/${lookupType}`,
      request
    );
  }

  // Specific methods for each lookup type
  listCountries(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Countries, request);
  }

  listCurrencies(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Currencies, request);
  }

  listCustomers(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Customers, request);
  }

  listVendors(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Vendors, request);
  }

  listContacts(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Contacts, request);
  }

  listAccounts(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Accounts, request);
  }

  listProducts(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Products, request);
  }

  listProductCategories(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.ProductCategories, request);
  }

  listProductGroups(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.ProductGroups, request);
  }

  listTaxCategories(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.TaxCategories, request);
  }

  listTaxExceptions(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.TaxExceptions, request);
  }

  listPriceLevels(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.PriceLevels, request);
  }

  listUnits(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Units, request);
  }

  listRoles(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Roles, request);
  }

  listJournalTypes(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.JournalTypes, request);
  }

  listAccountTypes(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.AccountType, request);
  }

  listAccountCategories(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.AccountCategories, request);
  }

  listBranches(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Branches, request);
  }

  listStores(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Stores, request);
  }

  listCompanies(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Companies, request);
  }

  listEmployees(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Employees, request);
  }

  listUsers(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Users, request);
  }

  listProductsWithUnits(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.ProductsWithUnits, request);
  }

  listGlAccountWithCategories(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.GlAccountWithCategories, request);
  }

  listDocumentTypes(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.DocumentTypes, request);
  }

  listFinancialStatementItems(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.FinancialStatementItem, request);
  }

  listDocumentSubTypes(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.DocumentSubType, request);
  }

  listFinancialYears(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.FinancialYear, request);
  }

  listTemplateEvents(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.TemplateEvents, request);
  }

  listShifts(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Shifts, request);
  }

  listShiftCategories(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.ShiftCategories, request);
  }

  listPages(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Pages, request);
  }

  listModules(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Modules, request);
  }

  listPageTypes(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.PageTypes, request);
  }

  listTenants(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Tenants, request);
  }

  listSubscriptionPackages(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.SubscriptionPackages, request);
  }

  listZatcaStates(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.ZatcaStates, request);
  }

  listShiftStatuses(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.ShiftStatuses, request);
  }

  listPurchaseRequest(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.PurchaseRequest, request);
  }

  listPurchaseOrders(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.PurchaseOrder, request);
  }

  listOfferPrices(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.OfferPrices, request);
  }

  listPurchaseInvoices(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.PurchaseInvoices, request);
  }

  listDocuments(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.Documents, request);
  }

  listSalesOrders(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.SalesOrders, request);
  }
  
  listPaymentMethods(request: PaginatedLookupRequest): Observable<PaginationResponse<SimpleLookupDto>> {
    return this.getList(LookupType.PaymentMethodLookup, request);
  }

  // Helper method to create a basic lookup request
  createBasicLookupRequest(
    term: string = '',
    pageNumber: number = 1,
    pageSize: number = 20,
    orderBy: string[] = [],
    category: string = 'ALL'
  ): PaginatedLookupRequest {
    return {
      term,
      advancedFilter: [],
      includeDeleted: false,
      pageNumber,
      pageSize,
      orderBy,
      // lookupType: '',
      category
    };
  }
}
